import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Folder } from "lucide-react";
import { Button, SearchField } from "@/design-system/components/ui";
import { foldersApi } from "@/api/folders";
import { cn } from "@/design-system/lib/utils";
import type { DocumentFolderTreeDto } from "@/types/api";
import { FolderTree } from "./FolderTree";

/**
 * Where the bounded in-scope tree comes from. `entity` resolves an entity's
 * folder root by type+id (deal staging, EC review); `folder` loads the subtree
 * rooted at a known folder id (DMS "Move to folder", scoped to one record).
 */
export type FolderPickerTreeSource =
  | { kind: "entity"; entityType: string; entityId: string }
  | { kind: "folder"; rootFolderId: string };

export interface FolderPickerSearchConfig {
  placeholder: string;
  /** Optional category filter forwarded to the server search (e.g. "Deal"). */
  category?: string;
}

/** Owner metadata for the chosen folder, so callers can re-anchor a tree. */
export interface FolderPickerSelection {
  entityType?: string | null;
  entityId?: string | null;
}

interface FolderPickerProps {
  value: string | null;
  onChange: (folderId: string, folder?: FolderPickerSelection) => void;
  /**
   * Bounded tree shown for in-scope browsing. Omit for a search-only picker.
   * An `entity` scope whose tree has not been materialised yet is provisioned
   * on demand (404-then-ensure); only an owner type with no registered
   * template at all falls back to the paginated "browse all folders" list
   * rather than a full client-side load.
   */
  treeSource?: FolderPickerTreeSource | null;
  /** Enables the debounced, server-paginated search box. Omit to hide it. */
  search?: FolderPickerSearchConfig;
  /**
   * When true, the paginated folder list browses (empty query) on mount, so
   * users can page through folders with "Load more" without typing a search.
   * Requires `search`. The in-scope tree (if any) still renders below.
   */
  browseByDefault?: boolean;
  /** Folder that must not be selectable (e.g. a document's current folder). */
  disabledFolderId?: string | null;
  className?: string;
}

/** Page size for the server-side folder search — Prompt: 25 + "Load more". */
const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 250;
const MIN_SEARCH_CHARS = 2;

/**
 * The one shared file-into-folder picker. A debounced server-side search
 * (`foldersApi.searchFolders`, paged 25 at a time with a "Load more" control)
 * sits above an optional bounded folder tree for the active scope. No surface
 * client-loads an entire folder area: large browses page through the server.
 */
export function FolderPicker({
  value,
  onChange,
  treeSource,
  search,
  browseByDefault,
  disabledFolderId,
  className,
}: FolderPickerProps) {
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");

  // Server-side search is the seam, so debounce typing instead of firing a
  // request per keystroke. Changing the debounced term resets paging because
  // it changes the infinite-query key.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [term]);

  const tree = useQuery({
    queryKey: ["folder-picker-tree", treeSource],
    queryFn: async () => {
      const src = treeSource!;
      if (src.kind === "folder") return foldersApi.tree(src.rootFolderId);
      // The entity GET is deliberately side-effect free and 404s whenever an
      // owner's tree has simply never been materialised — which is routine,
      // and says nothing about whether a template exists. Follow the contract
      // FoldersController documents ("clients call this once ... on a 404 from
      // the GET") before concluding anything: only if the ensure ALSO fails is
      // the owner type genuinely untemplated, which is the one case the amber
      // banner below describes accurately. Naming is SERVER-authoritative, so
      // pass null and let the root-name resolver's policy name win.
      try {
        return await foldersApi.entityTree(src.entityType, src.entityId);
      } catch {
        return await foldersApi.ensureEntityTree(src.entityType, src.entityId, null);
      }
    },
    enabled: Boolean(treeSource),
    retry: false,
  });

  const hasTree = Boolean(tree.data);
  // Untemplated entity owner — the GET 404'd *and* the ensure could not
  // provision one — so there is no tree to render and we browse the whole
  // folder set through the paginated search list (this replaces the old
  // full-set "__all" client load). The amber banner renders only for this case.
  const noTreeFallback =
    Boolean(search) &&
    treeSource?.kind === "entity" &&
    tree.isError;
  // Browse (empty query) by default when the caller asks, or as the
  // untemplated fallback — so "Load more" paging is available without typing.
  const browseMode = Boolean(search) && (Boolean(browseByDefault) || noTreeFallback);

  const hasQuery = debounced.length >= MIN_SEARCH_CHARS;
  const searchActive = Boolean(search) && (hasQuery || browseMode);

  const results = useInfiniteQuery({
    queryKey: ["folder-picker-search", search?.category ?? null, debounced, browseMode],
    queryFn: ({ pageParam }) =>
      foldersApi.searchFolders({
        q: hasQuery ? debounced : undefined,
        category: search?.category ?? undefined,
        page: pageParam,
        pageSize: PAGE_SIZE,
      }),
    enabled: searchActive,
    initialPageParam: 1,
    getNextPageParam: (last) => {
      const { page, pageSize, totalCount } = last.hits;
      return page * pageSize < totalCount ? page + 1 : undefined;
    },
  });

  const items = useMemo(
    () => results.data?.pages.flatMap((p) => p.hits.data) ?? [],
    [results.data],
  );
  const total = results.data?.pages[0]?.hits.totalCount ?? 0;

  const selectFolder = (id: string, folder?: FolderPickerSelection) => {
    if (disabledFolderId && id === disabledFolderId) return;
    onChange(id, folder);
  };

  return (
    <div className={cn("flex flex-col gap-space-3", className)}>
      {search && (
        <SearchField
          value={term}
          onChange={setTerm}
          placeholder={search.placeholder}
        />
      )}

      {noTreeFallback && (
        <p className="m-0 rounded border border-amber-300 bg-amber-50 px-space-3 py-space-2 text-caption text-amber-800">
          No default folder template for this owner type — browse all folders
          below, or pick a destination manually.
        </p>
      )}

      {searchActive && (
        <div className="flex flex-col gap-space-2">
          {results.isLoading ? (
            <p className="m-0 text-small text-gray-mid">Searching folders…</p>
          ) : items.length === 0 ? (
            <p className="m-0 text-small text-gray-mid">No matching folders.</p>
          ) : (
            <>
              <ul
                role="listbox"
                aria-label="Folder search results"
                className="m-0 flex max-h-56 list-none flex-col gap-space-1 overflow-auto rounded border border-gray-light bg-paper-warm p-space-1"
              >
                {items.map((f) => {
                  const selected = value === f.id;
                  const disabled = Boolean(disabledFolderId) && f.id === disabledFolderId;
                  return (
                    <li key={f.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        disabled={disabled}
                        onClick={() => selectFolder(f.id, { entityType: f.entityType, entityId: f.entityId })}
                        className={cn(
                          "flex w-full items-center gap-space-2 rounded px-space-3 py-space-2 text-left text-small text-onyx",
                          "hover:bg-white focus-visible:bg-white focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40",
                          selected && "bg-white font-semibold",
                        )}
                      >
                        <Folder size={14} aria-hidden className="shrink-0 text-gold" />
                        <span className="truncate">{f.name}</span>
                        {f.entityType && (
                          <span className="ml-auto shrink-0 text-caption text-gray-mid">
                            {f.entityType}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="flex items-center justify-between gap-space-2">
                <span className="text-caption text-gray-mid">
                  Showing {items.length} of {total}
                </span>
                {results.hasNextPage && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => results.fetchNextPage()}
                    loading={results.isFetchingNextPage}
                    disabled={results.isFetchingNextPage}
                  >
                    Load more
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {treeSource && tree.isLoading && (
        <p className="m-0 text-small text-gray-mid">Loading folder tree…</p>
      )}
      {hasTree && (
        <FolderTree
          nodes={[tree.data as DocumentFolderTreeDto]}
          selectedId={value}
          onSelect={(node) => selectFolder(node.id, { entityType: node.entityType, entityId: node.entityId })}
          disabledFolderId={disabledFolderId}
          initiallyExpanded
        />
      )}
      {!search && treeSource && tree.isError && (
        <p className="m-0 text-small text-gray-mid">
          No folders are available for this record.
        </p>
      )}
    </div>
  );
}
